/**
 * StructuredReport — renders the Claude-authored survey report produced
 * by the analysis-agent one-shot ``survey-structured-report`` task.
 *
 * Shape of the input:
 *
 *   {
 *     markdown: "## Introduction\n\n... ![chart](attachment://abc123) ...",
 *     attachments: [
 *       { attachment_id: "abc123", filename: "theme-frequency.png",
 *         content_type: "image/png", blob_url: "https://.../…?<SAS>", ... },
 *       ...
 *     ],
 *     agent_task_id: "…",
 *     generated_at: "…",
 *   }
 *
 * Differences from the older <OverallAnalysis>:
 *
 *   - Single markdown string (vs an array of report_response strings).
 *     The playbook produces a coherent narrative; we don't split it.
 *   - Chart PNGs are referenced via ``![alt](attachment://<id>)``. We
 *     override react-markdown's ``img`` renderer to resolve those refs
 *     against the ``attachments`` array (looking up ``blob_url`` by
 *     ``attachment_id``) and render a real ``<img>``. Other image
 *     schemes pass through untouched.
 *   - Each chart gets a small download button pinned to the top-right
 *     corner — matches the affordance Deep Research already offers so
 *     the pattern's consistent across the product.
 *
 * When the markdown references an attachment we don't have metadata for
 * (mismatch between saved attachments list and markdown refs — should
 * never happen but defensively coded for), we render a small "chart
 * unavailable" placeholder rather than a broken image icon. Same for
 * refs whose blob_url has somehow expired.
 */
import ErrorBoundary from '@components/ErrorBoundary';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { Box, Card, CardContent, IconButton, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';
import Markdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { AddToReportButton } from './AddToReportButton';

// The playbook tells Claude to produce frequency/comparison tables with
// GFM syntax; without remark-gfm they render as raw pipe characters and
// look broken. The plugin also gives us task lists and strikethrough at
// zero extra cost.
const REMARK_PLUGINS = [remarkGfm];

// react-markdown v9 sanitizes URLs via defaultUrlTransform, which strips
// any protocol not in its safelist (http, https, mailto, xmpp, ircs...).
// Our chart refs are ![alt](attachment://<id>) — a custom scheme our
// ChartImage renderer resolves to a real Azure blob URL. Without this
// override, react-markdown zeros the src, the <img> renders empty, and
// the customer sees nothing where a chart should be. Whitelist
// attachment:// through the transform, keep the default sanitization
// for every other URL (safer against a hypothetical Claude regression
// that inlines javascript:...).
function urlTransformWithAttachments(url, key) {
  if (typeof url === 'string' && url.startsWith('attachment://')) {
    return url;
  }
  return defaultUrlTransform(url, key);
}

/**
 * Convert the flat attachments array into an id→attachment map so the
 * markdown renderer can look up each ref in O(1) instead of scanning
 * the array on every chart.
 */
function useAttachmentMap(attachments) {
  return useMemo(() => {
    const map = new Map();
    (attachments || []).forEach((a) => {
      if (a?.attachment_id) map.set(a.attachment_id, a);
    });
    return map;
  }, [attachments]);
}

/**
 * Custom ``img`` element renderer for react-markdown.
 *
 * Handles two cases:
 *   1. ``attachment://<id>``: look up in the attachment map, render the
 *      real blob URL wrapped in a card with a top-right download button.
 *      Unknown id → placeholder card ("chart unavailable").
 *   2. Anything else (http/https/data): pass through as a plain <img>.
 *      This preserves any inline images Claude might inject via other
 *      schemes without needing special handling.
 */
function ChartImage({ src, alt, attachmentMap }) {
  if (!src?.startsWith('attachment://')) {
    return <img src={src} alt={alt || ''} style={{ maxWidth: '100%' }} />;
  }
  const attachmentId = src.slice('attachment://'.length);
  const attachment = attachmentMap.get(attachmentId);
  if (!attachment?.blob_url) {
    return (
      <Box
        sx={{
          my: 2,
          p: 2,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          color: 'text.secondary',
          textAlign: 'center',
          fontSize: 13,
        }}
      >
        Chart unavailable ({alt || attachment?.filename || attachmentId})
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        position: 'relative',
        my: 2,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fafafa',
      }}
    >
      <img
        src={attachment.blob_url}
        alt={alt || attachment.filename || 'Chart'}
        style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }}
      />
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          display: 'inline-flex',
        }}
      >
        <Tooltip title="Download chart">
          <IconButton
            size="small"
            component="a"
            href={attachment.blob_url}
            download={attachment.filename || `chart-${attachmentId}.png`}
            sx={{
              bgcolor: 'rgba(255,255,255,0.85)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
            }}
          >
            <DownloadRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export function StructuredReport({ report }) {
  const markdown = report?.markdown || '';
  const attachmentMap = useAttachmentMap(report?.attachments);

  const components = useMemo(
    () => ({
      img: ({ src, alt }) => <ChartImage src={src} alt={alt} attachmentMap={attachmentMap} />,
    }),
    [attachmentMap],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ position: 'absolute', right: -40, top: 0, zIndex: 9 }}>
        <ErrorBoundary FallbackComponent={() => null}>
          <AddToReportButton name={'Structured Report'} description={markdown} />
        </ErrorBoundary>
      </Box>
      <Card variant="outlined" sx={{ mx: 'auto', p: 0, mb: 2 }}>
        <CardContent
          sx={{
            p: '16px !important',
            color: '#000 !important',
            '& p': { margin: '8px 0', color: '#000 !important', lineHeight: 1.6 },
            '& h2': { margin: '20px 0 10px 0', color: '#7d5ae8' },
            '& h3': { margin: '18px 0 8px 0', color: '#333' },
            '& h4': { margin: '14px 0 6px 0', color: '#555' },
            '& ul, & ol': { marginTop: '4px', paddingLeft: '24px' },
            '& li': { margin: '4px 0' },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              my: 2,
              fontSize: 14,
            },
            '& th, & td': {
              border: '1px solid #e0e0e0',
              padding: '8px 12px',
              textAlign: 'left',
            },
            '& th': { bgcolor: '#f5f5f5', fontWeight: 600 },
            '& blockquote': {
              borderLeft: '3px solid #7d5ae8',
              margin: '10px 0',
              paddingLeft: 2,
              color: 'text.secondary',
              fontStyle: 'italic',
            },
            '& code': {
              bgcolor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: 1,
              fontSize: 13,
            },
          }}
        >
          <Markdown
            components={components}
            remarkPlugins={REMARK_PLUGINS}
            urlTransform={urlTransformWithAttachments}
          >
            {markdown}
          </Markdown>
          {report?.generated_at ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
              Generated {new Date(report.generated_at).toLocaleString()}
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
}

export default StructuredReport;
